import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { TApiErrorResponse, TNetworkError } from '@/services/errors';
import type { TSupabaseClient } from '@/services/supabase';

import { ERROR_CODES, errorUtils } from '@/services/errors';
import { resolveSupabaseClient } from '@/services/supabase';

import type {
  TCountResult,
  TList,
  TListResult,
  TQueryResult,
  TRequestConfig,
  TVoidResult,
} from './api.types';

import { apiClient } from './httpClient';

abstract class BaseService {
  protected abstract readonly serviceName: string;

  private readonly api: AxiosInstance = apiClient;

  private client: TSupabaseClient | null = null;

  protected get db(): TSupabaseClient {
    if (!this.client) {
      this.client = resolveSupabaseClient();
    }

    return this.client;
  }

  protected callSite(where: string): string {
    return `${this.serviceName}.${where}`;
  }

  private async request<TResponse>(
    config: AxiosRequestConfig,
    where: string,
  ): Promise<TResponse | TNetworkError> {
    try {
      const response: AxiosResponse<TResponse> = await this.api.request<TResponse>(config);

      return response.data;
    } catch (error) {
      return errorUtils.fromAxios(error as AxiosError<TApiErrorResponse>, this.callSite(where));
    }
  }

  protected get<TResponse, TParams = unknown>(
    url: string,
    config: TRequestConfig<TParams>,
  ): Promise<TResponse | TNetworkError> {
    const { where, ...rest } = config;

    return this.request<TResponse>({ ...rest, url, method: 'get' }, where);
  }

  protected post<TResponse, TBody = unknown, TParams = unknown>(
    url: string,
    body: TBody,
    config: TRequestConfig<TParams>,
  ): Promise<TResponse | TNetworkError> {
    const { where, ...rest } = config;

    return this.request<TResponse>({
      ...rest, url, method: 'post', data: body,
    }, where);
  }

  protected run<TRow>(
    query: PromiseLike<TQueryResult<TRow>>,
    where: string,
  ): Promise<TRow | TNetworkError>;

  protected run<TRow, TModel>(
    query: PromiseLike<TQueryResult<TRow>>,
    where: string,
    adapt: (row: TRow) => TModel,
  ): Promise<TModel | TNetworkError>;

  protected async run<TRow, TModel>(
    query: PromiseLike<TQueryResult<TRow>>,
    where: string,
    adapt?: (row: TRow) => TModel,
  ): Promise<TRow | TModel | TNetworkError> {
    const callSite = this.callSite(where);
    const { data, error } = await query;

    if (error) {
      return errorUtils.fromPostgrest(error, callSite);
    }

    if (data === null) {
      return errorUtils.createNetworkError(ERROR_CODES.unknown, undefined, callSite);
    }

    if (!adapt) {
      return data;
    }

    return adapt(data);
  }

  protected runList<TRow>(
    query: PromiseLike<TListResult<TRow>>,
    where: string,
  ): Promise<TList<TRow> | TNetworkError>;

  protected runList<TRow, TModel>(
    query: PromiseLike<TListResult<TRow>>,
    where: string,
    adapt: (row: TRow) => TModel,
  ): Promise<TList<TModel> | TNetworkError>;

  protected async runList<TRow, TModel>(
    query: PromiseLike<TListResult<TRow>>,
    where: string,
    adapt?: (row: TRow) => TModel,
  ): Promise<TList<TRow> | TList<TModel> | TNetworkError> {
    const callSite = this.callSite(where);
    const { data, error, count } = await query;

    if (error) {
      return errorUtils.fromPostgrest(error, callSite);
    }

    const rows = data ?? [];
    const total = count ?? rows.length;

    if (!adapt) {
      return { rows, total };
    }

    return { rows: rows.map(adapt), total };
  }

  protected async runCount(
    query: PromiseLike<TCountResult>,
    where: string,
  ): Promise<number | TNetworkError> {
    const callSite = this.callSite(where);
    const { error, count } = await query;

    if (error) {
      return errorUtils.fromPostgrest(error, callSite);
    }

    return count ?? 0;
  }

  protected async runVoid(
    query: PromiseLike<TVoidResult>,
    where: string,
  ): Promise<undefined | TNetworkError> {
    const { error } = await query;

    if (error) {
      return errorUtils.fromPostgrest(error, this.callSite(where));
    }

    return undefined;
  }
}

export {
  BaseService,
};
